/**
 * A message to each person in a group of a room, sent privately: it lands in each person's own thread with you,
 * and an email links them to it when the switch is on. One press is one send, however often the request arrives.
 */
import { useState } from "react";
import type { PrivateSendRequest, TalkAnswerGroup } from "../../../shared/talk";
import type { RoomFace, RoomGroupKey } from "../../../shared/types";
import { MergeBox } from "../../components/MergeBox";
import { Button } from "../../components/ui/Button";
import { FaceStack } from "../../components/ui/Face";
import { Field } from "../../components/ui/Field";
import { Icon } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { Toggle } from "../../components/ui/Toggle";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { type CircleSeed, useStartCircle } from "../../talk/handoff";

/** The design's merge word, and the one the server fills in per person. */
const FIRST_NAME = "{first name}";

/** The message as the server reads it: each person's first name filled in there. */
export function messageBody(text: string): string {
  return text.trim().replaceAll(FIRST_NAME, "{{first name}}");
}

/** The message a sheet opens with: the greeting, the group's words and the host's name. */
export function startMessage(words: string, hostFirst: string): string {
  return `Hi ${FIRST_NAME},\n\n${words}${hostFirst ? `\n\n${hostFirst}` : ""}`;
}

interface SendWords {
  title: string;
  reach: number;
  faces: Pick<RoomFace, "name" | "avatar_url">[];
  /** "Missed it, Tuesday 27 October". */
  label: string;
  subject: string;
  message: string;
  /** What already goes to this group by itself, said once under the message. */
  auto?: string;
  /** The group has nothing to send (or nobody to send to): the sheet offers only a circle. */
  circleOnly?: boolean;
  /** Carry the room's slides with the note. */
  slides?: boolean;
}

export interface PrivateSend extends SendWords {
  /** The group it is for: the same seed Start a circle opens with. */
  seed: CircleSeed;
}

/** The people who picked one choice of a poll in a recording. */
export interface AnswerSend extends SendWords {
  answer: TalkAnswerGroup;
}

export function PrivateSendSheet({
  send,
  onClose,
  onSent,
  onAnswerSent,
}: {
  /** Mounted fresh per group (keyed), so its words start from that group's. */
  send: PrivateSend | AnswerSend;
  onClose: () => void;
  /** A group's send. */
  onSent?: (key: RoomGroupKey, sent: number) => void;
  /** A poll choice's send. */
  onAnswerSent?: (sent: number) => void;
}) {
  const toast = useToast();
  const startCircle = useStartCircle();
  const [subject, setSubject] = useState(send.subject);
  const [message, setMessage] = useState(send.message);
  const [email, setEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Why one id per opened sheet: a second press while the first is in flight, or a retry, is the same send.
  const [requestId] = useState(() => crypto.randomUUID());

  const go = () => {
    if (busy) return;
    setBusy(true);
    setError("");
    const to: PrivateSendRequest["to"] =
      "seed" in send
        ? { group: { webinarId: send.seed.webinarId, scope: send.seed.scope, key: send.seed.key, ...(send.seed.session ? { session: send.seed.session } : {}) } }
        : { answer: send.answer };
    api.talk
      .sendPrivately({
        to,
        body: messageBody(message),
        email,
        ...(email ? { subject: subject.trim() } : {}),
        ...(send.slides ? { slides: true } : {}),
        requestId,
      })
      .then((res) => {
        if ("seed" in send) onSent?.(send.seed.key, res.sent);
        else onAnswerSent?.(res.sent);
        toast({ text: `Sent to ${res.sent.toLocaleString("en-US")} ${res.sent === 1 ? "person" : "people"}.`, icon: "send" });
        onClose();
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : "That didn't send. Try again."))
      .finally(() => setBusy(false));
  };

  const shown = send.faces.slice(0, 5);
  const ready = message.trim() !== "" && (!email || subject.trim() !== "");
  return (
    <Sheet
      open
      wide
      onClose={onClose}
      title={send.title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          {send.circleOnly ? null : (
            <Button variant="p" icon="send" disabled={busy || !ready || send.reach === 0} onClick={go}>
              {busy ? "Sending…" : `Send to ${send.reach.toLocaleString("en-US")}`}
            </Button>
          )}
        </>
      }
    >
      <div className="col" style={{ gap: 14 }}>
        <div className="row g12">
          <FaceStack people={shown.map((f) => ({ name: f.name, photo: f.avatar_url }))} size={32} more={Math.max(0, send.reach - shown.length)} />
          <div>
            <div className="b7">
              {send.reach.toLocaleString("en-US")} {send.reach === 1 ? "person" : "people"}
            </div>
            <div className="small muted">{send.label}</div>
          </div>
        </div>
        {"seed" in send ? (
          <div>
            <Button
              size="s"
              icon="target"
              onClick={() => {
                startCircle(send.seed);
                onClose();
              }}
            >
              Start a circle with them
            </Button>
          </div>
        ) : null}
        {send.circleOnly ? null : (
          <>
        <div className="row g12">
          <Toggle on={email} onChange={setEmail} label="Also email it" />
          <span>Also email it</span>
        </div>
        {email ? (
          <Field label="Subject" htmlFor="gsub">
            <input id="gsub" className="inp" value={subject} maxLength={160} onChange={(e) => setSubject(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Message" htmlFor="gbody" error={error || undefined}>
          <MergeBox id="gbody" label="Message" value={message} onChange={setMessage} />
        </Field>
        {send.auto ? (
          <div className="note brass">
            <Icon name="zap" />
            <div>{send.auto}</div>
          </div>
        ) : null}
          </>
        )}
      </div>
    </Sheet>
  );
}
